import { z } from "zod";

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
/** A saved destination within this performance; never a URL or model-selected target. */
export const zStoryDestination = z.object({ sceneId: id, pathId: id }).strict();
export type StoryDestination = z.infer<typeof zStoryDestination>;
/** Author direction is loaded from the saved score; response requests cannot override it. */
export const zStoryInteractionPolicy = z
  .object({
    intent: z.enum(["supportive-grief", "story"]),
    /** Lasting author-defined purpose. Audience answers may personalize the journey, not replace this outcome. */
    outcome: z.string().trim().min(1).max(2000).optional(),
    /** Replaceable goal for this invitation only, not a task to repeat in every reply. */
    step: z.string().trim().min(1).max(2000).optional(),
    guidance: z.string().max(4000).optional(),
    maxResponseBeats: z.number().int().min(4).max(24).optional(),
    /** Total scenes in one answer; omitted allows up to three when the response needs a change of setting or time. */
    maxResponseScenes: z.number().int().min(1).max(6).optional(),
    /** Server-authored clarification returns to the original invitation's scope after a touch choice. */
    clarificationSource: z
      .object({
        sceneId: id,
        beatId: id,
      })
      .strict()
      .optional(),
  })
  .strict();
export type StoryInteractionPolicy = z.infer<typeof zStoryInteractionPolicy>;
/** What the editor adds for a question without one; a reply never fails for lack of it. */
export const DEFAULT_RESPONSE_POLICY: StoryInteractionPolicy = { intent: "story" };

const legacy = z
  .object({
    kind: z.enum(["question", "practice"]),
    /** Authored audience-facing text; kind and repeatFromBeatId determine behavior. */
    continueLabel: z.string().trim().min(1).max(60).optional(),
    /** An earlier beat in the same scene to revisit as a complete movement. */
    repeatFromBeatId: z.string().min(1).max(100).optional(),
  })
  .strict();

/** A character invites a response. The exact saved scene/beat is the immutable fork boundary. */
export const zStoryResponseInteraction = z
  .object({
    kind: z.literal("respond"),
    speakerId: id,
    audienceName: z.string().min(1).max(100).optional(),
    /** The user can direct an existing onstage character, or respond directly as an audience member. */
    responseRole: z
      .discriminatedUnion("kind", [
        z.object({ kind: z.literal("character"), characterId: id }).strict(),
        z.object({ kind: z.literal("audience") }).strict(),
      ])
      .optional(),
    prompt: z.string().min(1).max(500),
    suggestions: z.array(z.string().min(1).max(200)).max(4),
    /** These audience replies resume the authored beats instead of requesting a generated response. */
    continueOn: z.array(z.string().min(1).max(200)).max(4).optional(),
    /** Authored audience choices jump to saved scenes, without generating or saving a reply. */
    choiceDestinations: z.array(z.object({
      answer: z.string().min(1).max(200),
      destination: zStoryDestination,
    }).strict()).min(1).max(4).optional(),
    /** Rejoin this saved scene on a continueOn reply, or after a generated authored handoff. */
    continueTo: zStoryDestination.optional(),
    /** Direct continueOn answers only. Omitted/immediate preserves the existing jump; generated handoffs are unchanged. */
    continueToTiming: z.enum(["immediate", "after-scene"]).optional(),
    inputPlaceholder: z.string().max(200).optional(),
    submitLabel: z.string().max(60).optional(),
    allowFreeText: z.boolean().optional(),
    /** Omitted/dialog: existing response dialog. Choices: authored touch choices. Cards: inline replies with optional free text. */
    presentation: z.enum(["dialog", "choices", "cards"]).optional(),
    /** Omitted true: the viewer may skip this beat and continue the authored story. False waits for a response. */
    optional: z.boolean().optional(),
    /** Optional on shared playback projections; required in the saved authoring beat before generation. */
    policy: zStoryInteractionPolicy.optional(),
  })
  .strict()
  .superRefine((interaction, context) => {
    const routedAnswers = new Set<string>();
    for (const [index, choice] of (interaction.choiceDestinations ?? []).entries()) {
      const answer = normalizedStoryAnswer(choice.answer);
      if (interaction.responseRole?.kind === "character" ||
        !interaction.suggestions.some(s => normalizedStoryAnswer(s) === answer) ||
        routedAnswers.has(answer) ||
        interaction.continueOn?.some(s => normalizedStoryAnswer(s) === answer))
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["choiceDestinations", index],
          message: "A saved destination needs a unique audience suggestion, separate from continueOn." });
      routedAnswers.add(answer);
    }
    if (
      (interaction.presentation === "choices" || interaction.allowFreeText === false) &&
      !interaction.suggestions.length
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["suggestions"],
        message: "An invitation without typed responses needs at least one authored choice.",
      });
    if (
      interaction.responseRole?.kind === "character" &&
      interaction.responseRole.characterId === interaction.speakerId
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["responseRole", "characterId"],
        message: "Choose a different character to receive the user's words.",
      });
  });
export type StoryResponseInteraction = z.infer<typeof zStoryResponseInteraction>;
export const zStoryInteraction = z.union([legacy, zStoryResponseInteraction]);
export type StoryInteraction = z.infer<typeof zStoryInteraction>;

const normalizedStoryAnswer = (value: string) =>
  value.trim().toLocaleLowerCase().replace(/[’‘]/g, "'").replace(/[.!?]+$/, "");

/** Only a saved authored choice can select a scene; audience input cannot supply a destination. */
export function authoredChoiceDestination(interaction: StoryResponseInteraction, answer: string): StoryDestination | undefined {
  if (interaction.responseRole?.kind === "character") return;
  return interaction.choiceDestinations?.find(choice =>
    normalizedStoryAnswer(choice.answer) === normalizedStoryAnswer(answer))?.destination;
}

/** An explicit author choice; ordinary invitations keep their generated responses. */
export function continuesAuthoredStory(interaction: StoryResponseInteraction, answer: string): boolean {
  return interaction.responseRole?.kind !== "character" &&
    (!!authoredChoiceDestination(interaction, answer) ||
      !!interaction.continueOn?.some((choice) => normalizedStoryAnswer(choice) === normalizedStoryAnswer(answer)));
}

/** Never share private author direction with audience screens. */
export function audienceStoryInteraction(interaction: StoryInteraction): StoryInteraction {
  if (interaction.kind !== "respond") return interaction;
  const { policy: _policy, ...publicInteraction } = interaction;
  return publicInteraction;
}
